
import React, { useState } from 'react';
import { DataConnector } from '../../types';
import { Check } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface ConnectorItemProps {
  connector: DataConnector;
  isSelected: boolean;
  onSelect: (connector: DataConnector) => void;
  disabled?: boolean;
}

const ConnectorItem: React.FC<ConnectorItemProps> = ({ 
  connector, 
  isSelected, 
  onSelect, 
  disabled = false 
}) => {
  const [imageError, setImageError] = useState(false);

  const getConnectorIcon = (connector: DataConnector) => {
    if (imageError) {
      return null;
    }
    
    if (connector.icons && connector.icons.length > 0) {
      return connector.icons[0];
    }
    if (connector.icon_url) {
      return connector.icon_url;
    }
    return connector.logoUrl || connector.icon || null;
  };

  // Alternative icon URLs for specific connectors
  const getAlternativeIcon = (name: string) => {
    const nameLC = name.toLowerCase();
    if (nameLC.includes('shopify')) {
      return 'https://cdn.shopify.com/shopifycloud/brochure/assets/brand-assets/shopify-logo-primary-logo-456baa801ee66a0a435671082365958316831c9960c480451dd0330bcdae304f.svg';
    }
    if (nameLC.includes('walmart')) {
      return 'https://www.logo.wine/a/logo/Walmart/Walmart-Logo.wine.svg';
    }
    if (nameLC.includes('amazon')) {
      return 'https://upload.wikimedia.org/wikipedia/commons/4/4a/Amazon_icon.svg';
    }
    if (nameLC.includes('google')) {
      return 'https://www.gstatic.com/images/branding/product/1x/sheets_48dp.png';
    }
    if (nameLC.includes('netsuite')) {
      return 'https://cdn.prod.website-files.com/6130fa1501794e37c21867cf/6191a3b11a9912c7c4c63be1_613293e85e476629514bfdef_NetSuite.svg'
    }
    if (nameLC.includes('veeqo')) {
      // Served locally (public/logos/veeqo.svg) rather than hotlinked -- the prior
      // Brandfetch asset URL returned 403 Access Denied (hotlinking not permitted),
      // which silently fell through to the letter-avatar fallback for every request.
      return '/logos/veeqo.svg';
    }
    return null;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div 
      data-testid={`connector-item-${connector.id}`}
      className={`py-3 px-3 border-b cursor-pointer transition-colors rounded-md hover:bg-gray-50 flex items-center justify-between ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
      onClick={() => !disabled && onSelect(connector)}
    >
      <div className="flex items-center">
        <Avatar className="flex-shrink-0 w-9 h-9 mr-4">
          <AvatarImage
            src={imageError ? getAlternativeIcon(connector.name) : getConnectorIcon(connector)}
            alt={connector.name}
            onError={handleImageError}
          />
          <AvatarFallback>{connector.name.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        
        <div className="font-medium text-brand-basic">
          {connector.name}
        </div>
      </div>
      
      <div className="flex-shrink-0">
        <div 
          className={`w-6 h-6 rounded-full border-2 ${
            isSelected
              ? 'border-brand-primary bg-brand-primary'
              : 'border-gray-300'
          }`}
        >
          {isSelected && (
            <div className="w-full h-full flex items-center justify-center">
              <Check className="text-white" size={16} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectorItem;
